/**
 * Connector IPC/HTTP errors may arrive as Error objects whose `.message` is itself
 * structured JSON or a plain object. UI must never surface `[object Object]`.
 */

export type StructuredErrorPayload = {
  code?: string;
  message?: string;
  error?: string;
  holder?: string;
};

/** Coerce any error fragment to readable text — objects become JSON, never [object Object]. */
export function normalizeErrorPart(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Raw error text for parsing — prefers string fields, JSON-stringifies structured bodies. */
export function errorText(error: unknown): string {
  if (error instanceof Error) {
    const msg = normalizeErrorPart(error.message);
    if (msg.trim()) return msg.trim();
  }
  if (typeof error === 'string') return error.trim();
  if (error != null && typeof error === 'object') {
    const record = error as Record<string, unknown>;
    for (const key of ['message', 'error', 'detail', 'reason'] as const) {
      const part = normalizeErrorPart(record[key]);
      if (part.trim()) return part.trim();
    }
    if (typeof record.code === 'string' && record.code.trim()) {
      return record.code.trim();
    }
    const serialized = normalizeErrorPart(record);
    if (serialized && serialized !== '{}') return serialized;
  }
  return String(error ?? '');
}

function parseStructuredPayload(text: string): StructuredErrorPayload | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const jsonStart = trimmed.indexOf('{');
  const candidate = jsonStart >= 0 ? trimmed.slice(jsonStart) : trimmed;

  try {
    const parsed = JSON.parse(candidate) as Record<string, unknown>;
    if (parsed == null || typeof parsed !== 'object') return null;

    const out: StructuredErrorPayload = {};
    if (typeof parsed.code === 'string') out.code = parsed.code;
    if (typeof parsed.message === 'string') out.message = parsed.message;
    if (typeof parsed.error === 'string') out.error = parsed.error;
    if (typeof parsed.holder === 'string') out.holder = parsed.holder;

    if (!out.code && !out.message && !out.error) return null;
    return out;
  } catch {
    return null;
  }
}

/** User-visible error — structured message/code, or readable text; never `[object Object]`. */
export function formatStructuredErrorMessage(error: unknown): string {
  const text = errorText(error).trim();
  if (!text) return 'error';

  const structured = parseStructuredPayload(text);
  if (structured) {
    if (structured.message?.trim()) return structured.message.trim();
    if (structured.error?.trim()) return structured.error.trim();
    if (structured.code?.trim()) return structured.code.trim();
  }

  if (text.includes('[object Object]')) {
    const rescue = normalizeErrorPart(error).trim();
    if (rescue && !rescue.includes('[object Object]')) {
      const rescued = parseStructuredPayload(rescue);
      if (rescued?.message?.trim()) return rescued.message.trim();
      if (rescued?.error?.trim()) return rescued.error.trim();
      if (rescued?.code?.trim()) return rescued.code.trim();
      if (rescue !== '{}') return rescue;
    }
    return 'error';
  }

  return text;
}
